/**
 * MentionText — render a user message that may contain canonical mention
 * markers `@[Title](type:id)` as styled pills interleaved with plain text
 * (issue #1457).
 *
 * Used by the user message bubble: the bubble's `displayContent` keeps the
 * marker form (the agent receives stripped text separately), so the reader sees
 * a distinct `@Title` pill per referenced object. Text with no markers renders
 * verbatim, so this is safe to use on any user content.
 */
import { Fragment } from 'react';
import { MENTION_MARKER_RE } from '@/features/chat/utils/mention-parser';

export interface MentionTextProps {
  text: string;
}

export function MentionText({ text }: MentionTextProps) {
  // Global clone (the exported singleton is non-global and stateful).
  const re = new RegExp(MENTION_MARKER_RE.source, 'g');
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const [full, title, type, id] = m;
    if (m.index > lastIndex) {
      nodes.push(<Fragment key={key++}>{text.slice(lastIndex, m.index)}</Fragment>);
    }
    nodes.push(
      <span key={key++} className="mention-pill" data-mention-type={type} data-mention-id={id} title={`${type}: ${title}`}>
        {title}
      </span>,
    );
    lastIndex = m.index + full.length;
  }
  if (lastIndex < text.length) {
    nodes.push(<Fragment key={key++}>{text.slice(lastIndex)}</Fragment>);
  }
  // No markers → render the original text unchanged.
  if (nodes.length === 0) return <>{text}</>;
  return <>{nodes}</>;
}
